import React, { useState, useEffect } from 'react'
import { Box, TextField, Typography, Checkbox, FormControlLabel, Alert } from '@mui/material'
import { useTranslation } from 'react-i18next'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_VARIANTS,
  BUTTON_THEME,
  BUTTON_SIZE,
} from '@hapana/shared/web/components/shared/button/button.constants'
import posthog from 'posthog-js'
import { useSupportAssistant } from '../support-assistant.hooks'
import { useMutation, Mutation } from '@hapana/shared/web/services/gqty'

interface TicketFormProps {
  userInfo?: {
    firstName: string
    lastName: string
    email: string
    userId?: string
  }
}

export function TicketForm({ userInfo }: TicketFormProps) {
  const { t } = useTranslation('core')
  const {
    messages,
    chatId,
    setCurrentView,
    addTicketMessage,
    planTier,
    userRole,
    pathname,
    pageName,
    brandId,
    brandName,
    locationId,
    locationName,
  } = useSupportAssistant()

  // Form state
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [includeChatHistory, setIncludeChatHistory] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // GraphQL mutation
  const [createTicket] = useMutation(
    (mutation: Mutation, args) => {
      const result = mutation.createSupportTicket(args)

      // Access fields to ensure they're fetched
      if (result) {
        void result.ticketId
        void result.ticketUrl
        void result.contactId
      }

      return result
    },
    {
      onComplete: (data) => {
        console.log('✅ Ticket created successfully:', data)
        setIsSubmitting(false)

        // Add ticket message to chat
        addTicketMessage(subject, description)

        // Show success message
        setSuccess(true)

        // Navigate back to chat view after short delay
        setTimeout(() => {
          setCurrentView('chat')
          // Reset form
          setSubject('')
          setDescription('')
          setIncludeChatHistory(true)
          setSuccess(false)
        }, 1500)
      },
      onError: (err) => {
        console.error('❌ Failed to create ticket:', err)
        setError(t('supportAssistant.errors.ticketCreationFailed'))
        setIsSubmitting(false)
      },
    }
  )

  // Auto-populate subject from first user message
  useEffect(() => {
    if (messages.length > 0 && !subject) {
      const firstUserMessage = messages.find((m) => m.role === 'user')
      if (firstUserMessage) {
        const truncatedSubject = firstUserMessage.content.substring(0, 100)
        setSubject(truncatedSubject)
      }
    }
  }, [messages, subject])

  const handleSubmit = async () => {
    // Validation
    if (!subject.trim()) {
      setError(t('supportAssistant.errors.subjectRequired'))
      return
    }
    if (!description.trim()) {
      setError(t('supportAssistant.errors.descriptionRequiredTicket'))
      return
    }
    if (!userInfo?.firstName || !userInfo?.lastName || !userInfo?.email) {
      setError(t('supportAssistant.errors.userInfoMissing'))
      return
    }

    setIsSubmitting(true)
    setError(null)

    // Prepare chat history if included
    const chatHistory = includeChatHistory
      ? messages.map((msg) => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.createdAt,
        }))
      : undefined

    // Call GraphQL mutation to create HubSpot ticket
    createTicket({
      args: {
        input: {
          subject,
          description,
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          email: userInfo.email,
          userId: userInfo.userId || undefined,
          brandId: brandId || undefined,
          brandName: brandName || undefined,
          locationId: locationId || undefined,
          locationName: locationName || undefined,
          chatId: chatId || undefined,
          chatHistory,
          userRole,
          planTier,
          pathname,
          pageName,
          currentUrl: window.location.href,
          environment: navigator.userAgent,
          posthogSessionUrl: posthog.get_session_replay_url?.() || undefined,
          posthogDistinctId: posthog.get_distinct_id?.() || undefined,
        },
      },
    })
  }

  const handleCancel = () => {
    setCurrentView('chat')
  }

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {success ? (
        <Alert severity="success">{t('supportAssistant.ticket.success')}</Alert>
      ) : (
        <>
          {error && (
            <Alert severity="error" data-testid="ticket-form-error">
              {error}
            </Alert>
          )}

          {/* User Info (disabled fields) */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}
            >
              {t('supportAssistant.ticket.contactInfo')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label={t('supportAssistant.ticket.firstName')}
                value={userInfo?.firstName || ''}
                disabled
                size="small"
                data-testid="ticket-first-name"
              />
              <TextField
                fullWidth
                label={t('supportAssistant.ticket.lastName')}
                value={userInfo?.lastName || ''}
                disabled
                size="small"
                data-testid="ticket-last-name"
              />
            </Box>

            <TextField
              fullWidth
              label={t('supportAssistant.ticket.email')}
              value={userInfo?.email || ''}
              disabled
              size="small"
              data-testid="ticket-email"
            />
          </Box>

          {/* Ticket Details */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}
            >
              {t('supportAssistant.ticket.ticketDetails')}
            </Typography>

            <TextField
              fullWidth
              label={t('supportAssistant.ticket.subject')}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={isSubmitting}
              size="small"
              sx={{ mb: 2 }}
              data-testid="ticket-subject"
              inputProps={{ maxLength: 200 }}
            />

            <TextField
              fullWidth
              label={t('supportAssistant.ticket.description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSubmitting}
              multiline
              rows={6}
              placeholder={t('supportAssistant.ticket.descriptionPlaceholder')}
              data-testid="ticket-description"
            />
          </Box>

          {/* Include Chat History Checkbox */}
          {messages.length > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeChatHistory}
                  onChange={(e) => setIncludeChatHistory(e.target.checked)}
                  disabled={isSubmitting}
                  data-testid="ticket-include-chat"
                />
              }
              label={
                <Typography variant="body2">
                  {t('supportAssistant.ticket.includeChatHistory')} (
                  {t('supportAssistant.ticket.messageCount', { count: messages.length })})
                </Typography>
              }
            />
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2 }}>
            <Button
              onClick={handleCancel}
              disabled={isSubmitting}
              variant={BUTTON_VARIANTS.OUTLINED}
              theme={BUTTON_THEME.SECONDARY}
              size={BUTTON_SIZE.MEDIUM}
              label={t('supportAssistant.ticket.cancel')}
              data-testid="ticket-cancel-button"
            />
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !subject.trim() || !description.trim()}
              variant={BUTTON_VARIANTS.CONTAINED}
              theme={BUTTON_THEME.PRIMARY}
              size={BUTTON_SIZE.MEDIUM}
              label={
                isSubmitting
                  ? t('supportAssistant.ticket.submitting')
                  : t('supportAssistant.ticket.submit')
              }
              data-testid="ticket-submit-button"
            />
          </Box>
        </>
      )}
    </Box>
  )
}
